import { MultiKeyValueComponent, NoValueDash } from '../common';
import * as React from 'react';
import { toHex } from 'ui/common/hex-converter';
import { IconDisplay } from 'ui/common/icon';
import { Label } from 'ui/common/label/label';
import { ValueDisplay } from 'ui';
import { compose } from 'redux';
import get from 'lodash/get';
import styles from './display-codes.scss';

export interface DisplayCodesProps extends MultiKeyValueComponent<number> {
  /**
   * Display modes defined by design handbook
   */
  style?: 'primary' | 'secondary';
  /**
   * When true, label font is normal and the value font is bold and uppercase
   */
  invertStrong?: boolean;
  /**
   * When true, the label is displayed normal and the value font is bold
   */
  strong?: boolean;
  /**
   * When true, the background color defined for the current value is used as background color
   */
  displayBackgroundColor?: boolean;
  /**
   * Currently only 'left' is supported
   */
  displayIcon?: 'left' | 'right';
  /**
   * When true, the label is positioned above the value, as opposed to default, where label is to the left of the value
   */
  showStacked?: boolean;
  /**
   * When true, the component gets hidden when value is empty
   */
  hideIfNoValue?: boolean;
  /**
   * Text label to display
   */
  textLabel?: string;
}

interface DisplayCodeItemProps {
  valueItem: any;
  suffix: string;
  displayBackgroundColor: boolean;
  displayIcon?: 'left' | 'right';
}

const DisplayCodeItem = ({ valueItem, suffix, displayBackgroundColor, displayIcon }: DisplayCodeItemProps) => {
  const backgroundColor = displayBackgroundColor ? toHex(valueItem.backgroundColor) : '';
  const style = backgroundColor
    ? { backgroundColor, borderColor: backgroundColor, color: toHex(valueItem.foregroundColor) }
    : undefined;
  const iconStyle = displayIcon === 'left' ? styles.displayIconLeft : styles.displayIconRight;

  return (
    <span className={backgroundColor ? 'has-bg-color' : ''} style={style}>
      <IconDisplay classNames={iconStyle} visible={displayIcon} url={displayIcon && get(valueItem, 'icon')} />
      <ValueDisplay valueItem={valueItem} suffix={suffix}/>
    </span>
  );
};

const DisplayCodesComponent = (props: DisplayCodesProps) => {
  const {
    className,
    componentId,
    displayBackgroundColor = true,
    displayIcon,
    hideIfNoValue = false,
    label,
    valueItems = [],
    textLabel } = props;

  const items = valueItems
    .filter(n => n && n.value > 0)
    .map((valueItem, i) => !!valueItem.description && (
      <DisplayCodeItem
        key={i}
        valueItem={valueItem}
        suffix={i < valueItems.length - 1 ? ', ' : ''}
        displayBackgroundColor={displayBackgroundColor === true}
        displayIcon={displayIcon}
      />
    ));

  if (hideIfNoValue && !items.length) {
    return false;
  }

  return (
    <div className={className} id={componentId}>
      {textLabel ? <span className={styles.textLabel}>{textLabel}</span> : <Label label={label} />}
      {items.length ? <div>{items}</div> : <NoValueDash/>}
    </div>
  );
};

/**
 * A utility that maps known props to a known style and appends them to a list.
 *
 * The list of final style classes are concatenated into a valid JSX className string attribute
 *
 * @param props
 */
const styleFromProps = (props: DisplayCodesProps) =>
  [
    styles.code,
    props.className,
    props.showStacked && styles.stackedColumn,
    props.invertStrong && styles.invertStrong,
    props.strong && !props.invertStrong && styles.strong,
    !!styles[props.style] && `${styles[props.style]} display-codes-styled` ,
    !!get(props, 'valueItem.icon') && props.displayIcon === 'left' && styles.displayIconLeft
  ].filter(s => s).join(' ');

/**
 * Higher order component to override the final className
 *
 * @param Component the component to decorate
 */
const styleVariations = (Component: any) => (props: DisplayCodesProps) => (
  <Component {...props} className={styleFromProps(props)} />
);

export const DisplayCodes = compose(styleVariations)(DisplayCodesComponent);
